import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shield, Search, AlertCircle } from 'lucide-react';
import { whitelistApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';

export const WhitelistManager = () => {
    const { language } = useLanguage();
    const [whitelist, setWhitelist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDiscordId, setNewDiscordId] = useState('');
    const [newNote, setNewNote] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [adding, setAdding] = useState(false);

    const t = {
        de: {
            title: 'Discord Whitelist',
            description: 'Whitelistete Benutzer können sich ohne die "Guild Member" Rolle einloggen.',
            discordId: 'Discord ID',
            note: 'Notiz (optional)',
            add: 'Hinzufügen',
            remove: 'Entfernen',
            search: 'Whitelist durchsuchen...',
            noEntries: 'Keine Einträge in der Whitelist',
            addedOn: 'Hinzugefügt am',
            confirmRemove: 'Wirklich entfernen?',
            success: {
                added: 'Zur Whitelist hinzugefügt',
                removed: 'Von Whitelist entfernt'
            },
            error: {
                load: 'Fehler beim Laden der Whitelist',
                add: 'Fehler beim Hinzufügen',
                remove: 'Fehler beim Entfernen',
                invalidId: 'Bitte gib eine gültige Discord ID ein',
                alreadyExists: 'Diese Discord ID ist bereits gewhitelisted'
            }
        },
        en: {
            title: 'Discord Whitelist',
            description: 'Whitelisted users can log in without the "Guild Member" role.',
            discordId: 'Discord ID',
            note: 'Note (optional)',
            add: 'Add',
            remove: 'Remove',
            search: 'Search whitelist...',
            noEntries: 'No entries in whitelist',
            addedOn: 'Added on',
            confirmRemove: 'Really remove?',
            success: {
                added: 'Added to whitelist',
                removed: 'Removed from whitelist'
            },
            error: {
                load: 'Error loading whitelist',
                add: 'Error adding to whitelist',
                remove: 'Error removing from whitelist',
                invalidId: 'Please enter a valid Discord ID',
                alreadyExists: 'This Discord ID is already whitelisted'
            }
        }
    };

    const texts = t[language] || t.en;

    useEffect(() => {
        loadWhitelist();
    }, []);

    const loadWhitelist = async () => {
        try {
            setLoading(true);
            const response = await whitelistApi.getAll();
            setWhitelist(response.data.whitelist || []);
        } catch (error) {
            console.error('Failed to load whitelist:', error);
            toast.error(texts.error.load);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newDiscordId.trim()) {
            toast.error(texts.error.invalidId);
            return;
        }

        // Validate Discord ID format (should be a number, typically 17-19 digits)
        if (!/^\d{17,19}$/.test(newDiscordId.trim())) {
            toast.error(texts.error.invalidId);
            return;
        }

        try {
            setAdding(true);
            await whitelistApi.add(newDiscordId.trim(), newNote.trim() || null);
            toast.success(texts.success.added);
            setNewDiscordId('');
            setNewNote('');
            loadWhitelist();
        } catch (error) {
            console.error('Failed to add to whitelist:', error);
            if (error.response?.status === 400) {
                toast.error(texts.error.alreadyExists);
            } else {
                toast.error(texts.error.add);
            }
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (discordId) => {
        if (!window.confirm(texts.confirmRemove)) return;

        try {
            await whitelistApi.remove(discordId);
            toast.success(texts.success.removed);
            loadWhitelist();
        } catch (error) {
            console.error('Failed to remove from whitelist:', error);
            toast.error(texts.error.remove);
        }
    };

    const filteredWhitelist = whitelist.filter(entry =>
        entry.discord_id?.includes(searchTerm) ||
        entry.note?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                    <Shield size={24} className="text-green-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold font-['Rajdhani'] text-white">
                        {texts.title}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                        {texts.description}
                    </p>
                </div>
            </div>

            {/* Add New Entry */}
            <div className="p-4 rounded-xl border border-white/10 bg-white/5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            {texts.discordId}
                        </label>
                        <Input
                            type="text"
                            placeholder="123456789012345678"
                            value={newDiscordId}
                            onChange={(e) => setNewDiscordId(e.target.value)}
                            className="bg-black/40 border-white/10"
                            data-testid="whitelist-discord-id-input"
                        />
                    </div>
                    <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                            {texts.note}
                        </label>
                        <Input
                            type="text"
                            placeholder="z.B. Tester, VIP..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="bg-black/40 border-white/10"
                            data-testid="whitelist-note-input"
                        />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                        <Button
                            onClick={handleAdd}
                            disabled={adding || !newDiscordId.trim()}
                            className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold"
                            data-testid="whitelist-add-button"
                        >
                            <Plus size={18} className="mr-2" />
                            {texts.add}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                    type="text"
                    placeholder={texts.search}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-11 bg-black/40 border-white/10"
                    data-testid="whitelist-search-input"
                />
            </div>

            {/* Whitelist Table */}
            <div className="rounded-xl border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">
                        {language === 'de' ? 'Laden...' : 'Loading...'}
                    </div>
                ) : filteredWhitelist.length === 0 ? (
                    <div className="p-8 text-center">
                        <AlertCircle size={48} className="mx-auto mb-4 text-gray-600" />
                        <p className="text-gray-400">{texts.noEntries}</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {filteredWhitelist.map((entry) => (
                            <div
                                key={entry.id || entry.discord_id}
                                className="flex items-center justify-between p-4 bg-card/30 hover:bg-white/5 transition-colors"
                                data-testid={`whitelist-entry-${entry.discord_id}`}
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                                        <Shield size={18} className="text-green-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-white font-semibold">
                                                {entry.discord_id}
                                            </span>
                                        </div>
                                        {entry.note && (
                                            <p className="text-sm text-gray-500 truncate">
                                                {entry.note}
                                            </p>
                                        )}
                                        {entry.created_at && (
                                            <p className="text-xs text-gray-600">
                                                {texts.addedOn}: {formatDate(entry.created_at)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemove(entry.discord_id)}
                                    className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                                    data-testid={`whitelist-remove-${entry.discord_id}`}
                                >
                                    <Trash2 size={18} />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Info Box */}
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
                <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200/80">
                        {language === 'de' ? (
                            <>
                                <strong>Hinweis:</strong> Benutzer müssen trotzdem Mitglied auf dem Discord-Server sein.
                                Die Whitelist erlaubt nur das Einloggen ohne die "Guild Member" Rolle.
                            </>
                        ) : (
                            <>
                                <strong>Note:</strong> Users still need to be members of the Discord server.
                                The whitelist only allows login without the "Guild Member" role.
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
